/**
 * Alpine.js component factories for the website-analytics dashboard charts
 * (Overview tab's visitors/sessions trend + top-sources bar; Traffic tab's
 * pageviews/visits dual-axis trend + breakdown bars; Audience tab's
 * breakdown bars; Conversions tab's funnel). Each factory receives
 * pre-formatted data from Go templates (translated series names, dates
 * already localized to the viewer's timezone) and creates an ApexCharts
 * instance.
 *
 * Generic chart machinery (theme color resolution, render queue, ApexCharts
 * loader, base options, theme-change re-render) lives in `chart-core.ts` and is
 * imported below — this module never re-implements it. Every factory's returned
 * object is wrapped in `themeAware(...)` so a light/dark toggle rebuilds the
 * chart with fresh colors. Modeled on the shapes in `review-charts.ts`.
 */

import {
  hexAlpha,
  themeColors,
  waitForApexCharts,
  waitForVisible,
  enqueueRender,
  baseOpts,
  themeAware,
  resetChartMount,
} from "./chart-core";

/** Standard axis label style (matches review-charts.ts's copy). */
function axisLabelStyle() {
  const t = themeColors();
  return { colors: t.mutedText, fontSize: "11px", fontWeight: 400 };
}

/** Axis-title style — the "what does this axis show" label (e.g. "Date"). */
function axisTitle(text: string | undefined) {
  const t = themeColors();
  return { text: text || "", style: { color: t.mutedText, fontSize: "12px", fontWeight: 600 } };
}

// Format a numeric value per the chart data's optional valueDecimals +
// valueSuffix. Default (0 decimals, no suffix) rounds to a whole number; the
// performance charts pass {valueDecimals:1, valueSuffix:' s'} to show "1.8 s".
function valueFormatter(data: any) {
  const dec = data.valueDecimals || 0;
  const suffix = data.valueSuffix || "";
  return (v: number) => (dec > 0 ? Number(v).toFixed(dec) : Math.round(v).toString()) + suffix;
}

function baseLegend() {
  const t = themeColors();
  return {
    legend: {
      labels: { colors: t.textColor },
      position: "bottom" as const,
      fontSize: "12px",
      markers: { size: 6, shape: "circle" as const },
      itemMargin: { horizontal: 12, vertical: 4 },
    },
  };
}

// Resolve a code (country/language) to its localized display name via the
// browser's CLDR data (Intl.DisplayNames) — no server-side locale table needed.
// Invalid codes or an unsupported runtime fall back to the raw code.
function safeDisplayName(dn: any, code: string): string {
  try {
    return dn.of(code) || code;
  } catch {
    return code;
  }
}

// Expand a breakdown's category codes to human labels based on data.codeType,
// using the viewer's locale (from <html lang>):
//   - "country": show the compact UPPER code in the bar, full name on hover.
//   - "language": show the full language name in the bar (codes like "en-US"
//     are cryptic, and there are few, so no tooltip needed).
//   - otherwise: labels are already display-ready (server-humanized) — leave them.
function expandBreakdownLabels(data: any): { categories: string[]; tooltipNames: string[] | null } {
  const categories: string[] = (data.categories || []).slice();
  let tooltipNames: string[] | null = null;
  const locale = document.documentElement.lang || "en";
  const hasIntl = typeof (Intl as any).DisplayNames === "function";
  try {
    if (data.codeType === "country" && hasIntl) {
      const rn = new (Intl as any).DisplayNames([locale], { type: "region" });
      tooltipNames = [];
      for (let i = 0; i < categories.length; i++) {
        const up = String(categories[i]).toUpperCase();
        categories[i] = up;
        tooltipNames[i] = safeDisplayName(rn, up);
      }
    } else if (data.codeType === "language" && hasIntl) {
      const ln = new (Intl as any).DisplayNames([locale], { type: "language" });
      for (let i = 0; i < categories.length; i++) {
        categories[i] = safeDisplayName(ln, String(categories[i]));
      }
    }
  } catch {
    /* Intl.DisplayNames unavailable → keep raw codes */
  }
  return { categories, tooltipNames };
}

// ---------------------------------------------------------------------------
// 1. Analytics Trend Chart — pageviews/visits over time, area chart
// ---------------------------------------------------------------------------
export function analyticsTrendChart(data: any) {
  return themeAware({
    chart: null as any,
    async init() {
      const self = this as any;
      await enqueueRender(async () => {
        await waitForApexCharts();
        await waitForVisible(self.$refs.chart);
        if (!self.$refs.chart?.isConnected) return;
        resetChartMount(self); // clear any orphan chart from the init/theme race
        const AC = (window as any).ApexCharts;
        const t = themeColors();
        const opts = baseOpts();
        const fmtV = valueFormatter(data);
        self.chart = new AC(self.$refs.chart, {
          ...opts,
          ...baseLegend(),
          chart: { ...opts.chart, type: "area" as const, height: data.height || 320 },
          series: data.series,
          stroke: { width: 2, curve: "smooth" as const },
          fill: { type: "gradient", gradient: { opacityFrom: 0.35, opacityTo: 0.02 } },
          xaxis: {
            categories: data.categories,
            title: axisTitle(data.xAxisLabel),
            labels: { style: axisLabelStyle() },
            axisBorder: { show: false },
            axisTicks: { show: false },
          },
          yaxis: {
            title: axisTitle(data.yAxisLabel),
            labels: { style: axisLabelStyle(), formatter: fmtV },
          },
          colors: [t.secondary, hexAlpha(t.textColor, 0.35)],
          markers: { size: 0, hover: { size: 5 } },
          tooltip: { ...opts.tooltip, y: { formatter: fmtV } },
        });
        self.chart.render();
      });
    },
    destroy() {
      this.chart?.destroy();
    },
  });
}

// ---------------------------------------------------------------------------
// 2. Analytics Dual Axis — dual Y-axis line chart (pageviews on the left axis,
//    visits on the right — different scales, same time buckets; a series may
//    still opt into type:'bar' via its data). Modeled on review-charts.ts's
//    scoreVolumeChart, but WITHOUT that chart's 0-10 score clamp
//    (min/max/tickAmount) — analytics counts have no fixed range, so both axes
//    auto-scale to their own series.
// ---------------------------------------------------------------------------
export function analyticsDualAxis(data: any) {
  return themeAware({
    chart: null as any,
    async init() {
      const self = this as any;
      await enqueueRender(async () => {
        await waitForApexCharts();
        await waitForVisible(self.$refs.chart);
        if (!self.$refs.chart?.isConnected) return;
        resetChartMount(self); // clear any orphan chart from the init/theme race
        const AC = (window as any).ApexCharts;
        const t = themeColors();
        const opts = baseOpts();
        self.chart = new AC(self.$refs.chart, {
          ...opts,
          ...baseLegend(),
          chart: { ...opts.chart, type: "line" as const, height: data.height || 320 },
          series: data.series,
          stroke: {
            width: data.series.map((s: any) => (s.type === "bar" ? 0 : 2)),
            curve: "smooth" as const,
          },
          xaxis: {
            categories: data.categories,
            title: axisTitle(data.xAxisLabel),
            labels: { style: axisLabelStyle() },
            axisBorder: { show: false },
            axisTicks: { show: false },
          },
          yaxis: [
            {
              seriesName: data.leftNames || [],
              title: {
                text: data.leftAxisLabel || "",
                style: { color: t.mutedText, fontSize: "11px" },
              },
              labels: {
                style: axisLabelStyle(),
                formatter: (v: number) => Math.round(v).toString(),
              },
            },
            {
              seriesName: data.rightNames || [],
              opposite: true,
              title: {
                text: data.rightAxisLabel || "",
                style: { color: t.mutedText, fontSize: "11px" },
              },
              labels: {
                style: axisLabelStyle(),
                formatter: (v: number) => Math.round(v).toString(),
              },
            },
          ],
          colors: [t.secondary, hexAlpha(t.textColor, 0.35)],
          markers: { size: 3 },
          tooltip: { ...opts.tooltip },
        });
        self.chart.render();
      });
    },
    destroy() {
      this.chart?.destroy();
    },
  });
}

// ---------------------------------------------------------------------------
// 3. Analytics Funnel — visitors -> property-page views -> booking click.
//    ApexCharts has NO native funnel chart type, so this renders the same
//    shape as analyticsBreakdownBar (horizontal bar, one category per step)
//    but with dataLabels enabled: each bar's label shows the raw visitor
//    count plus the pre-formatted, i18n-resolved drop-off text the Go
//    template built for that step (data.dropoffLabels[i], null for step 0 —
//    there is no prior step to drop off from).
// ---------------------------------------------------------------------------
export function analyticsFunnel(data: any) {
  return themeAware({
    chart: null as any,
    async init() {
      const self = this as any;
      await enqueueRender(async () => {
        await waitForApexCharts();
        await waitForVisible(self.$refs.chart);
        if (!self.$refs.chart?.isConnected) return;
        resetChartMount(self); // clear any orphan chart from the init/theme race
        const AC = (window as any).ApexCharts;
        const t = themeColors();
        const opts = baseOpts();
        const dropoffLabels: (string | null)[] = data.dropoffLabels || [];
        self.chart = new AC(self.$refs.chart, {
          ...opts,
          chart: { ...opts.chart, type: "bar" as const, height: data.height || 280 },
          series: data.series,
          plotOptions: {
            bar: { horizontal: true, barHeight: "55%", borderRadius: 3 },
          },
          dataLabels: {
            enabled: true,
            style: { colors: [t.textColor], fontSize: "12px", fontWeight: 600 },
            formatter: (val: number, o: any) => {
              const dropoff = dropoffLabels[o.dataPointIndex as number];
              return dropoff ? `${val}  (${dropoff})` : `${val}`;
            },
          },
          xaxis: {
            categories: data.categories,
            title: axisTitle(data.valueAxisLabel),
            labels: { style: axisLabelStyle(), formatter: (v: number) => Math.round(v).toString() },
          },
          yaxis: {
            labels: { style: axisLabelStyle() },
          },
          colors: [t.secondary],
          legend: { show: false },
          tooltip: { ...opts.tooltip },
        });
        self.chart.render();
      });
    },
    destroy() {
      this.chart?.destroy();
    },
  });
}

// ---------------------------------------------------------------------------
// 4. Analytics Breakdown Bar — horizontal top-N bar (e.g. top sources,
//    countries, devices, browsers, languages). data.codeType drives label
//    expansion (see expandBreakdownLabels): countries show a compact code in
//    the bar + full country name on hover; languages show the full language
//    name; everything else is already display-ready from the server.
// ---------------------------------------------------------------------------
export function analyticsBreakdownBar(data: any) {
  return themeAware({
    chart: null as any,
    async init() {
      const self = this as any;
      await enqueueRender(async () => {
        await waitForApexCharts();
        await waitForVisible(self.$refs.chart);
        if (!self.$refs.chart?.isConnected) return;
        resetChartMount(self); // clear any orphan chart from the init/theme race
        const AC = (window as any).ApexCharts;
        const t = themeColors();
        const opts = baseOpts();
        const fmtV = valueFormatter(data);
        const { categories, tooltipNames } = expandBreakdownLabels(data);
        self.chart = new AC(self.$refs.chart, {
          ...opts,
          chart: { ...opts.chart, type: "bar" as const, height: data.height || 320 },
          series: data.series,
          plotOptions: {
            bar: { horizontal: true, barHeight: "60%", borderRadius: 3 },
          },
          xaxis: {
            categories,
            title: axisTitle(data.valueAxisLabel),
            labels: { style: axisLabelStyle(), formatter: fmtV },
          },
          yaxis: {
            labels: { style: axisLabelStyle() },
          },
          colors: [t.secondary],
          legend: { show: false },
          tooltip: {
            ...opts.tooltip,
            y: { formatter: fmtV },
            // Country bars carry compact codes; show the full country
            // name (from Intl.DisplayNames) as the hover title instead.
            ...(tooltipNames
              ? { x: { formatter: (val: any, o: any) => tooltipNames[o?.dataPointIndex] ?? val } }
              : {}),
          },
        });
        self.chart.render();
      });
    },
    destroy() {
      this.chart?.destroy();
    },
  });
}

// ---------------------------------------------------------------------------
// 5. Analytics Heatmap — weekday × hour activity ("when do visitors come").
//    data.series is 7 rows (weekdays, bottom→top), each 24 {x:hour, y:count}
//    points; color intensity scales with the count (data.max).
// ---------------------------------------------------------------------------
export function analyticsHeatmap(data: any) {
  return themeAware({
    chart: null as any,
    async init() {
      const self = this as any;
      await enqueueRender(async () => {
        await waitForApexCharts();
        await waitForVisible(self.$refs.chart);
        if (!self.$refs.chart?.isConnected) return;
        resetChartMount(self); // clear any orphan chart from the init/theme race
        const AC = (window as any).ApexCharts;
        const t = themeColors();
        const opts = baseOpts();
        self.chart = new AC(self.$refs.chart, {
          ...opts,
          chart: { ...opts.chart, type: "heatmap" as const, height: data.height || 300 },
          series: data.series,
          dataLabels: { enabled: false },
          colors: [t.secondary],
          plotOptions: {
            heatmap: {
              radius: 2,
              enableShades: true,
              shadeIntensity: 0.65,
              colorScale: { min: 0, ...(data.max ? { max: data.max } : {}) },
            },
          },
          stroke: { width: 1, colors: [t.bgColor] },
          xaxis: {
            type: "category" as const,
            labels: { style: axisLabelStyle(), rotate: 0, hideOverlappingLabels: true },
            axisBorder: { show: false },
            axisTicks: { show: false },
          },
          yaxis: { labels: { style: axisLabelStyle() } },
          legend: { show: false },
          tooltip: { ...opts.tooltip },
        });
        self.chart.render();
      });
    },
    destroy() {
      this.chart?.destroy();
    },
  });
}

// ---------------------------------------------------------------------------
// 6. Analytics Sparkline — compact per-row trend (the admin spend
//    Organizations tab's 30-day cost sparkline, one per table row).
//    Modeled on review-charts.ts's platformMiniChart (same x-data/x-ref
//    mount + themeAware/enqueueRender/waitForApexCharts/resetChartMount
//    plumbing, and Alpine registration alongside it in studio-bundle.ts),
//    but NOT reused as-is: platformMiniChart hardcodes a 0-10 y-axis (the
//    rating scale its own callers need) which would visually clip a cost
//    series at 10 -- the SAME "no fixed range, auto-scale to the series"
//    reasoning analyticsDualAxis documents for dropping scoreVolumeChart's
//    clamp. Uses ApexCharts' own `sparkline` mode (auto-hides axes/grid/
//    legend, auto-scales to the series' own min/max) rather than a
//    manually-suppressed bar chart -- the mode exists for exactly this.
// ---------------------------------------------------------------------------
export function analyticsSparklineChart(data: any) {
  return themeAware({
    chart: null as any,
    async init() {
      const self = this as any;
      await enqueueRender(async () => {
        await waitForApexCharts();
        await waitForVisible(self.$refs.chart);
        if (!self.$refs.chart?.isConnected) return;
        resetChartMount(self); // clear any orphan chart from the init/theme race
        const AC = (window as any).ApexCharts;
        const t = themeColors();
        const fmtV = valueFormatter(data);
        self.chart = new AC(self.$refs.chart, {
          chart: {
            type: "bar" as const,
            height: data.height || 40,
            sparkline: { enabled: true },
            background: "transparent",
          },
          series: [{ name: data.seriesName || "", data: data.data || [] }],
          xaxis: { categories: data.categories || [] },
          plotOptions: { bar: { columnWidth: "70%" } },
          colors: [t.secondary],
          tooltip: {
            theme: document.documentElement.getAttribute("data-theme")?.includes("light")
              ? "light"
              : "dark",
            style: { fontSize: "12px" },
            y: { formatter: fmtV },
          },
        });
        self.chart.render();
      });
    },
    destroy() {
      this.chart?.destroy();
    },
  });
}

// ---------------------------------------------------------------------------
// Money formatting for the Bookings tab (L1). Values arrive in MAJOR units
// (e.g. 412.5) with the website's checkout currency; shown as whole units in
// the viewer's locale. An invalid currency code falls back to "<n> <code>".
// ---------------------------------------------------------------------------
export function moneyFormatter(locale?: string, currency?: string) {
  let nf: Intl.NumberFormat | null = null;
  try {
    nf = new Intl.NumberFormat(locale || undefined, {
      style: "currency",
      currency: currency || "EUR",
      maximumFractionDigits: 0,
    });
  } catch {
    nf = null;
  }
  return (v: number) => (nf ? nf.format(v) : `${Math.round(v)} ${currency || ""}`.trim());
}

const HTML_ESCAPES: Record<string, string> = {
  "&": "&amp;",
  "<": "&lt;",
  ">": "&gt;",
  '"': "&quot;",
  "'": "&#39;",
};

/**
 * Escape text bound for an ApexCharts slot that is written with innerHTML —
 * the tooltip title, series-name label and value (vendored v5.10.6,
 * tooltip Labels.DOMHandling). Axis labels are svg.js text nodes and must NOT
 * be escaped (they would show "&lt;" literally).
 */
export function escapeHtml(value: unknown): string {
  return (value == null ? "" : String(value)).replace(/[&<>"']/g, (c) => HTML_ESCAPES[c]);
}

/** Per-bar colors: muted bars (Matching…, Unknown) gray, the rest accent. */
export function campaignBarColors(muted: boolean[], accent: string, mutedColor: string): string[] {
  return muted.map((m) => (m ? mutedColor : accent));
}

/**
 * Blank all but ~maxLabels evenly spaced entries of a category axis, always
 * keeping the first and last. Same length as the input so the result can be
 * looked up by data-point index in an axis-label formatter (the tooltip keeps
 * the full categories). Pure; deterministic where ApexCharts' own overlap
 * hiding is not (it only drops labels it measures as colliding and can leave
 * dense "10.07.11.07.12.07." runs on a forced-horizontal category axis).
 */
export function thinTickLabels(categories: string[], maxLabels: number): string[] {
  const n = categories.length;
  if (n === 0) return [];
  const k = Math.min(n, Math.max(1, Math.floor(maxLabels)));
  if (k >= n) return [...categories];
  const out = categories.map(() => "");
  if (k === 1) {
    out[0] = categories[0];
    return out;
  }
  for (let i = 0; i < k; i++) {
    const idx = Math.round((i * (n - 1)) / (k - 1));
    out[idx] = categories[idx];
  }
  return out;
}

/** Axis-label budget for a chart `width` px wide: ~70px per label, 3..10 (6 when unknown). */
export function maxTickLabels(width?: number): number {
  if (!width || width <= 0) return 6;
  return Math.min(10, Math.max(3, Math.floor(width / 70)));
}

// ---------------------------------------------------------------------------
// 7. Analytics Revenue Chart (Bookings tab) — booked revenue per bucket as an
//    accent line with small markers (single-booking days stay visible on a
//    lumpy series), the previous period as a muted dashed line, ONE y-axis
//    (money, floor 0). X-axis labels are thinned to a width-based budget
//    (thinTickLabels); the tooltip title keeps every bucket's own date and the
//    tooltip appends the bucket's pre-formatted booking count
//    (data.bookingLabels[i], i18n plural from Go) to the revenue value.
// ---------------------------------------------------------------------------
export function analyticsRevenueChart(data: any) {
  return themeAware({
    chart: null as any,
    async init() {
      const self = this as any;
      await enqueueRender(async () => {
        await waitForApexCharts();
        await waitForVisible(self.$refs.chart);
        if (!self.$refs.chart?.isConnected) return;
        resetChartMount(self); // clear any orphan chart from the init/theme race
        const AC = (window as any).ApexCharts;
        const t = themeColors();
        const opts = baseOpts();
        const fmtMoney = moneyFormatter(data.locale, data.currency);
        const bookingLabels: string[] = data.bookingLabels || [];
        const series: any[] = data.series || [];
        const categories: string[] = data.categories || [];
        const tickLabels = thinTickLabels(categories, maxTickLabels(self.$refs.chart?.clientWidth));
        self.chart = new AC(self.$refs.chart, {
          ...opts,
          ...baseLegend(),
          chart: { ...opts.chart, type: "line" as const, height: data.height || 320 },
          series,
          // monotoneCubic never overshoots, so the curve cannot dip below the
          // zero floor between a booking day and an empty one.
          stroke: {
            width: series.map((_, i) => (i === 0 ? 2.5 : 1.5)),
            curve: "monotoneCubic" as const,
            dashArray: series.map((_, i) => (i === 0 ? 0 : 5)),
          },
          xaxis: {
            categories,
            // SVG text, never HTML-escaped. The budget thinning above decides
            // which labels show; rotate stays 0 so the kept ones never tilt.
            labels: {
              style: axisLabelStyle(),
              rotate: 0,
              hideOverlappingLabels: false,
              formatter: (value: string, _ts?: number, o?: any) => {
                const i = typeof o?.i === "number" ? o.i : categories.indexOf(value);
                return tickLabels[i] ?? "";
              },
            },
            axisBorder: { show: false },
            axisTicks: { show: false },
          },
          yaxis: { min: 0, labels: { style: axisLabelStyle(), formatter: fmtMoney } },
          colors: [t.secondary, hexAlpha(t.textColor, 0.45)],
          markers: { size: series.map((_, i) => (i === 0 ? 3 : 0)), strokeWidth: 0, hover: { size: 5 } },
          tooltip: {
            ...opts.tooltip,
            shared: true,
            intersect: false,
            // The axis formatter blanks thinned labels; the title must keep
            // each bucket's own date. innerHTML slot: escape it.
            x: { formatter: (val: unknown, o?: any) => escapeHtml(categories[o?.dataPointIndex] ?? val) },
            y: {
              formatter: (v: number, o: any) =>
                o?.seriesIndex === 0 && bookingLabels[o.dataPointIndex]
                  ? `${fmtMoney(v)} · ${bookingLabels[o.dataPointIndex]}`
                  : fmtMoney(v),
            },
          },
        });
        self.chart.render();
      });
    },
    destroy() {
      this.chart?.destroy();
    },
  });
}

// ---------------------------------------------------------------------------
// 8. Analytics Campaign Bar (Bookings tab) — sorted horizontal revenue bars,
//    copper per bar except Matching…/Unknown (gray, already sorted last by
//    the server). Labels carry their kind ("summer24 · campaign"); the
//    tooltip is a pre-formatted i18n line per bar (data.tooltips[i]).
// ---------------------------------------------------------------------------
export function analyticsCampaignBar(data: any) {
  return themeAware({
    chart: null as any,
    async init() {
      const self = this as any;
      await enqueueRender(async () => {
        await waitForApexCharts();
        await waitForVisible(self.$refs.chart);
        if (!self.$refs.chart?.isConnected) return;
        resetChartMount(self); // clear any orphan chart from the init/theme race
        const AC = (window as any).ApexCharts;
        const t = themeColors();
        const opts = baseOpts();
        const fmtMoney = moneyFormatter(data.locale, data.currency);
        const tooltips: string[] = data.tooltips || [];
        self.chart = new AC(self.$refs.chart, {
          ...opts,
          chart: { ...opts.chart, type: "bar" as const, height: data.height || 320 },
          series: [{ name: data.seriesName || "", data: data.data || [] }],
          plotOptions: {
            bar: { horizontal: true, barHeight: "60%", borderRadius: 3, distributed: true },
          },
          colors: campaignBarColors(data.muted || [], t.secondary, hexAlpha(t.textColor, 0.3)),
          dataLabels: { enabled: false },
          legend: { show: false },
          xaxis: {
            categories: data.categories,
            labels: { style: axisLabelStyle(), formatter: fmtMoney },
          },
          yaxis: { labels: { style: axisLabelStyle(), maxWidth: 220 } },
          // Bar names carry visitor-controlled utm/referrer text and ApexCharts
          // renders the tooltip via innerHTML: escape the title (the bar name)
          // and the value line. The y-axis labels themselves are SVG text.
          tooltip: {
            ...opts.tooltip,
            x: { formatter: (val: unknown) => escapeHtml(val) },
            y: {
              formatter: (v: number, o: any) =>
                escapeHtml(tooltips[o?.dataPointIndex] ?? fmtMoney(v)),
              title: { formatter: () => "" },
            },
          },
        });
        self.chart.render();
      });
    },
    destroy() {
      this.chart?.destroy();
    },
  });
}
